import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useExpensableGroupsDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { useQuickFiltersContext } from 'modules/expenses/components/expenses/PageContents/QuickFiltersContext'
import { useExpensesListPage } from 'modules/expenses/hooks/useExpensesListPage'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { datesToLocalString, isMyExpensesSection } from 'modules/expenses/lib/common'
import { usePageActions } from 'modules/expenses/pages/my_expenses/usePageActions'

import { Amount } from './_Amount'
import { Date as DateComponent } from './_Date'
import { Detail } from './_Detail'
import { ExpensablesCount } from './_ExpensablesCount'
import { allFilters } from './_Filters'
import { Owner } from './_Owner'
import { Status } from './_Status'

interface Props {
  employeeIds?: string[]
}

export const GroupsDataTable = ({ employeeIds }: Props) => {
  const { navigation } = useExpensesNavigation()
  const context = useOutletContext<RouteContextParams>()
  const { section: expensesContext, expensableGroupsEnabled } = context
  const { company } = useCurrentSession()
  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const { from, to } = datesToLocalString(dateFilters.startDate, dateFilters.endDate)
  const navigate = useNavigate()

  const { filters, clearFilters } = useQuickFiltersContext()
  const { status, pendingApproval } = filters ?? {}

  const includeDrafts = isMyExpensesSection(expensesContext)

  const {
    categories,
    subcategories,
    currencies,
    hasExpensesReportOnBehalfFeature,
    hasExpensesPerDiemsFeature,
    canCreateExpenses,
  } = useExpensesListPage({
    companyId: company.id,
  })

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { actions } = usePageActions({
    isInMyExpensesSection: isMyExpensesSection(expensesContext),
    canUsePerDiems: hasExpensesPerDiemsFeature,
    canCreateExpenses: canCreateExpenses,
    expensableGroupsEnabled: expensableGroupsEnabled,
  })

  const { primary } = actions
  const source = useExpensableGroupsDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('expenses_v2.groups_list.header.name'),
        kind: ProjectionKind.Custom,
        template: 'name',
        render: ({ name }) => name,
      },
      {
        hidden: isMyExpensesSection(expensesContext),
        identifier: 'owner',
        title: i18n.t('expenses_v2.list.header.owner'),
        kind: ProjectionKind.Custom,
        template: '{{ owner.access.fullName }}',
        render: ({ owner }) => {
          return <Owner owner={owner} />
        },
      },
      {
        identifier: 'status',
        title: i18n.t('expenses_v2.groups_list.header.status'),
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: ({ status: groupStatus }) => {
          return <Status status={groupStatus} />
        },
      },
      {
        identifier: 'expensablesCount',
        title: i18n.t('expenses_v2.groups_list.header.expensables_count'),
        kind: ProjectionKind.Custom,
        template: '{{ expensablesCount }}',
        render: ({ expensablesCount }) => {
          return <ExpensablesCount count={expensablesCount} />
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('expenses_v2.groups_list.header.amount'),
        kind: ProjectionKind.Custom,
        template: '{{ amount | divided_by: 100}} {{currency}}',
        render: ({ amount, currency }) => {
          return currency && <Amount amount={amount} currency={currency} />
        },
      },
      {
        identifier: 'reimbursableAmount',
        title: i18n.t('expenses_v2.list.header.reimbursable_amount'),
        kind: ProjectionKind.Custom,
        template: '{{ reimbursableAmount | divided_by: 100}} {{reimbursableCurrency}}',
        render: ({ reimbursableAmount, reimbursableCurrency }) => {
          return (
            reimbursableCurrency && (
              <Amount amount={String(reimbursableAmount)} currency={reimbursableCurrency} />
            )
          )
        },
      },
      {
        identifier: 'reviewRequestAt',
        title: i18n.t('expenses_v2.groups_list.header.review_request_at'),
        kind: ProjectionKind.Custom,
        template: '{{ reviewRequestAt | date: "%Y-%m-%d" }}',
        render: ({ reviewRequestAt }) => {
          return reviewRequestAt && <DateComponent date={reviewRequestAt} />
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        width: 70,
        render: ({ id, currency, amount, reviewRequestAt, owner }) => {
          if (!id) return null
          return (
            <Detail
              id={id}
              effectiveOn={reviewRequestAt}
              currency={currency}
              amount={amount}
              owner={owner}
            />
          )
        },
      },
    ],
    {
      variables: {
        from: from,
        employeeIds: employeeIds,
        to: to,
        includeDrafts,
        companyId: company.id,
        search: debouncedSearch,
      },
    }
  )

  const clearQuickFilters = () => {
    if (status || pendingApproval) clearFilters()
  }

  const datatableFilters = allFilters({
    expensesContext: expensesContext ?? 'company',
    source,
    currencies,
    categories,
    subcategories,
    initialFilterValues: {
      status,
      pendingApproval,
    },
    clearQuickFilters,
    includeReportedByFilter: hasExpensesReportOnBehalfFeature,
  })

  return (
    <>
      <Outlet context={{ ...context, back: { href: navigation.list.fullPath() } }} />
      <Box gap="s16">
        <DataTable
          disableExport
          disableEditColumns
          persistFilters={!status && !pendingApproval}
          enableClearFilters
          filters={datatableFilters}
          pagination={{
            kind: 'cursor',
            itemsPerPage: 25,
            infiniteScroll: true,
          }}
          source={source}
          sort={{ path: 'reviewRequestAt', direction: 'desc' }}
          selectable={() => false}
          search={{
            placeholder: i18n.t('common.search'),
            value: search,
            onChange: onSearch,
          }}
          actions={
            <>
              <DateNavigator
                startDate={dateFilters.startDate}
                endDate={dateFilters.endDate}
                onDateRangeChange={setPageDateFilters}
              />
              {primary && primary.length > 0 && (
                <PopoverMenu type="list">
                  <PopoverMenu.Trigger>
                    <Button.Decorative type="primary" iconName="add">
                      {i18n.t('command_bar.plugins.actions.my_expenses.add_expense')}
                    </Button.Decorative>
                  </PopoverMenu.Trigger>
                  <PopoverMenu.Content side="bottom" align="center">
                    {primary.map((action) => (
                      <PopoverMenu.TouchableOption
                        title={action.label}
                        key={action.key}
                        onClick={() => navigate(action.to)}
                      />
                    ))}
                  </PopoverMenu.Content>
                </PopoverMenu>
              )}
            </>
          }
        />
      </Box>
    </>
  )
}
